// Add a station (spec/13 §12, F-ORG-03, F-STN-02): a short Stepper in a centred dialog — Type → Details →
// Pair (BRAND.md §6.1, "steps, not a wall of fields"). The station is created when Details is confirmed;
// Pair then binds its PC with the code the PC shows, or is left for later from the list.
//
// A console, a sim, a VR set or a table is **run by staff** by default (ADR-0033): no app to pair, so the
// last step is Ready, not Pair — the station is on the floor and staff start its sessions from its tile.
// The Type step says how the station runs and lets staff override the kind's default either way.
//
// When no class fits, Details offers "New class", which turns the Details step into the class form, in place in
// this dialog (never a second dialog on top), and selects the class it creates — a fresh venue defines its
// first class without leaving the flow. Cancel goes back to Details with what was typed there.

import type { CreateStation, Station, StationClass, StationKind } from '@clavian/api-client';
import { stationManagedByDefault } from '@clavian/contracts/constants';
import { useTranslation } from '@clavian/i18n/react';
import { Button, cx, Dialog, FormSection, SelectField, Stepper, TextField, useToast } from '@clavian/ui-web';
import { useState, type FormEvent, type ReactNode } from 'react';

import { PROBLEM, problemCode } from '../api/problems';
import { useProblem } from '../api/use-problem';
import { Icon, NavIcon } from '../shell/nav-icons';
import { Notice, useStepFocus } from '../ui';
import { ClassForm } from './ClassesTab';
import { PairingForm } from './PairingForm';
import { useCreateStation, useStationClasses, useZones } from './queries';

/** The kinds the contract accepts (`StationKind`), in the order the Type step offers them. */
const KINDS: readonly StationKind[] = ['pc', 'console', 'sim', 'vr', 'table', 'other'];

const KIND_ICON: Readonly<Record<StationKind, ReactNode>> = {
  pc: <NavIcon name="stations" />,
  console: <NavIcon name="library" />,
  sim: <NavIcon name="fleet" />,
  vr: <Icon name="layers" />,
  table: <NavIcon name="catalogue" />,
  other: <Icon name="more" />,
};

interface FormState {
  readonly number: string;
  readonly name: string;
  readonly kind: StationKind;
  readonly classId: string;
  readonly zoneId: string;
  /** Paired to the station app (`true`) or run by staff; `null` follows the kind's default (ADR-0033). */
  readonly managed: boolean | null;
}

const EMPTY_FORM: FormState = { number: '', name: '', kind: 'pc', classId: '', zoneId: '', managed: null };

/** Whether the station being added pairs to the station app: the staff's pick, else the kind's default. */
export function managedOf(form: Pick<FormState, 'kind' | 'managed'>): boolean {
  return form.managed ?? stationManagedByDefault(form.kind);
}

export function AddStationDialog({
  organizationId,
  venueId,
  open,
  onOpenChange,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="md" dirty="auto" title={t('stations.add.title')} description={t('stations.add.intro')}>
      {/* Remounted on every open, so each station starts from a clean first step. */}
      {open ? <AddStationFlow organizationId={organizationId} venueId={venueId} onDone={() => onOpenChange(false)} /> : null}
    </Dialog>
  );
}

function AddStationFlow({ organizationId, venueId, onDone }: { readonly organizationId: string; readonly venueId: string; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [created, setCreated] = useState<Station | null>(null);
  const managed = created === null ? managedOf(form) : created.managed;

  return (
    <div className="console-stepper-flow">
      <Stepper
        current={step}
        onStepSelect={created === null ? setStep : undefined}
        steps={[
          // The step's summary once it is done; on the step itself the picked tile already says it.
          { id: 'type', label: t('stations.add.stepType'), ...(step > 0 ? { description: t(`stations.kind.${form.kind}`) } : {}) },
          { id: 'details', label: t('stations.add.stepDetails') },
          { id: 'pair', label: managed ? t('stations.add.stepPair') : t('stations.add.stepReady') },
        ]}
      />
      {step === 0 ? (
        <TypeStep
          kind={form.kind}
          managed={managedOf(form)}
          onChange={(kind) => setForm((current) => ({ ...current, kind, managed: null }))}
          onManagedChange={(value) => setForm((current) => ({ ...current, managed: value }))}
          onNext={() => setStep(1)}
          onCancel={onDone}
        />
      ) : step === 1 ? (
        <DetailsStep
          organizationId={organizationId}
          venueId={venueId}
          form={form}
          setForm={setForm}
          onBack={() => setStep(0)}
          onCreated={(station) => {
            setCreated(station);
            setStep(2);
          }}
        />
      ) : created !== null && !created.managed ? (
        <div className="console-form">
          <Notice tone="ok" role="status" title={t('stations.add.staffRunCreated', { station: created.name ?? t(`stations.kind.${created.kind}`) + ' ' + String(created.number) })}>
            {t('stations.add.staffRunReady')}
          </Notice>
          <div className="console-form__actions console-form__actions--end">
            <Button variant="primary" onClick={onDone}>
              {t('stations.add.done')}
            </Button>
          </div>
        </div>
      ) : created !== null ? (
        <div className="console-form">
          <Notice tone="ok" role="status" title={t('stations.add.created', { number: created.number })}>
            {t('stations.add.pairIntro')}
          </Notice>
          <PairingForm
            organizationId={organizationId}
            venueId={venueId}
            stationId={created.id}
            stationNumber={created.number}
            onPaired={() => {
              show({ tone: 'ok', title: t('stations.add.paired', { number: created.number }) });
              onDone();
            }}
            secondary={
              <Button variant="ghost" onClick={onDone}>
                {t('stations.add.pairLater')}
              </Button>
            }
          />
        </div>
      ) : null}
    </div>
  );
}

function TypeStep({
  kind,
  managed,
  onChange,
  onManagedChange,
  onNext,
  onCancel,
}: {
  readonly kind: StationKind;
  readonly managed: boolean;
  readonly onChange: (kind: StationKind) => void;
  readonly onManagedChange: (managed: boolean) => void;
  readonly onNext: () => void;
  readonly onCancel: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  return (
    <div className="console-form">
      <fieldset className="console-kind-picker">
        <legend className="clav-field__label">{t('stations.add.kind')}</legend>
        <div className="console-kind-picker__grid">
          {KINDS.map((option) => (
            <label key={option} className={cx('console-kind-picker__option', option === kind && 'is-checked')}>
              <input type="radio" className="clav-visually-hidden" name="station-kind" value={option} checked={option === kind} onChange={() => onChange(option)} />
              <span className="console-kind-picker__icon" aria-hidden="true">
                {KIND_ICON[option]}
              </span>
              <span className="console-kind-picker__name">{t(`stations.kind.${option}`)}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset className="console-run-by">
        <legend className="clav-field__label">{t('stations.add.runBy')}</legend>
        {([false, true] as const).map((option) => (
          <label key={String(option)} className={cx('console-run-by__option', option === managed && 'is-checked')}>
            <input type="radio" name="station-run-by" value={option ? 'app' : 'staff'} checked={option === managed} onChange={() => onManagedChange(option)} />
            <span className="console-run-by__name">{option ? t('stations.add.runByApp') : t('stations.add.runByStaff')}</span>
            <span className="console-run-by__hint">{option ? t('stations.add.runByAppHint') : t('stations.add.runByStaffHint')}</span>
          </label>
        ))}
      </fieldset>
      <div className="console-form__actions console-form__actions--end">
        <Button variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button variant="primary" onClick={onNext}>
          {t('common.next')}
        </Button>
      </div>
    </div>
  );
}

function DetailsStep({
  organizationId,
  venueId,
  form,
  setForm,
  onBack,
  onCreated,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly form: FormState;
  readonly setForm: (update: (current: FormState) => FormState) => void;
  readonly onBack: () => void;
  readonly onCreated: (station: Station) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const classes = useStationClasses(organizationId, venueId);
  const zones = useZones(organizationId, venueId);
  const create = useCreateStation(organizationId, venueId);
  const [errors, setErrors] = useState<{ number?: string; classId?: string; form?: string }>({});
  // "New class": the class form takes this step's place until it creates one or is cancelled.
  const [creatingClass, setCreatingClass] = useState(false);
  const { stepRef: classStepRef, opener: classOpener } = useStepFocus(creatingClass);

  const classOptions = (classes.data ?? []).map((klass) => ({ value: klass.id, label: klass.name }));
  const zoneOptions = [{ value: '', label: t('stations.add.zoneNone') }, ...(zones.data ?? []).map((zone) => ({ value: zone.id, label: zone.name }))];
  const noClasses = classes.data !== undefined && classes.data.length === 0;

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const next: { number?: string; classId?: string } = {};
    const parsed = Number(form.number);
    if (form.number.trim() === '' || !Number.isInteger(parsed) || parsed < 1) next.number = t('stations.add.required');
    if (form.classId === '') next.classId = t('stations.add.required');
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    const body: CreateStation = {
      number: parsed,
      ...(form.name.trim() === '' ? {} : { name: form.name.trim() }),
      kind: form.kind,
      classId: form.classId,
      ...(form.zoneId === '' ? {} : { zoneId: form.zoneId }),
      // Said only when staff chose against the kind's default (ADR-0033); the server applies the same default.
      ...(form.managed === null ? {} : { managed: form.managed }),
    };
    create.mutate(body, {
      onSuccess: (station) => {
        setErrors({});
        onCreated(station);
      },
      onError: (error) => {
        if (problemCode(error) === PROBLEM.conflict) setErrors({ number: t('stations.add.numberTaken') });
        else if (problem.fieldMessage(error, 'number') !== undefined) setErrors({ number: problem.fieldMessage(error, 'number') });
        else setErrors({ form: problem.text(error, { fallback: t('stations.add.refused') }) });
      },
    });
  }

  // When a class is created inline, select it so the station being added uses it straight away.
  function onClassCreated(klass: StationClass): void {
    setForm((current) => ({ ...current, classId: klass.id }));
    setErrors((current) => ({ ...current, classId: undefined }));
  }

  if (creatingClass) {
    return (
      <div ref={classStepRef} className="console-form">
        <div className="console-inline-step__head">
          <h3 className="console-inline-step__title">{t('stations.classes.new')}</h3>
          <p className="console-subtle">{t('stations.classes.drawerHint')}</p>
        </div>
        <ClassForm organizationId={organizationId} venueId={venueId} editing={undefined} onDone={() => setCreatingClass(false)} onCreated={onClassCreated} />
      </div>
    );
  }

  return (
    <form className="console-form" aria-label={t('stations.add.title')} noValidate onSubmit={submit}>
      {noClasses ? (
        <Notice
          tone="warn"
          action={
            <Button {...classOpener} type="button" variant="secondary" size="sm" onClick={() => setCreatingClass(true)}>
              {t('stations.classes.new')}
            </Button>
          }
        >
          {t('stations.add.noClasses')}
        </Notice>
      ) : null}
      {errors.form !== undefined ? (
        <Notice tone="danger" role="alert">
          {errors.form}
        </Notice>
      ) : null}
      {/* Two fields to add a PC (its number and its class); a label and a zone wait behind More options. */}
      <FormSection
        more={
          <>
            <TextField
              label={t('stations.add.name')}
              description={t('stations.add.nameHint')}
              showOptional
              value={form.name}
              onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
            />
            <SelectField
              label={t('stations.add.zone')}
              showOptional
              options={zoneOptions}
              value={form.zoneId}
              onValueChange={(value) => setForm((current) => ({ ...current, zoneId: value }))}
            />
          </>
        }
      >
      <TextField
        label={t('stations.add.number')}
        description={t('stations.add.numberHint')}
        required
        numeric
        inputMode="numeric"
        value={form.number}
        onChange={(event) => setForm((current) => ({ ...current, number: event.target.value }))}
        error={errors.number}
      />
      <div className="console-field-with-action">
        <SelectField
          label={t('stations.add.class')}
          required
          placeholder={t('stations.add.chooseClass')}
          options={classOptions}
          value={form.classId}
          onValueChange={(value) => setForm((current) => ({ ...current, classId: value }))}
          error={errors.classId}
        />
        {!noClasses ? (
          <Button {...classOpener} type="button" variant="ghost" size="sm" icon={<Icon name="plus" />} onClick={() => setCreatingClass(true)}>
            {t('stations.add.createClass')}
          </Button>
        ) : null}
      </div>
      </FormSection>
      <div className="console-form__actions console-form__actions--end">
        <Button type="button" variant="ghost" onClick={onBack}>
          {t('common.back')}
        </Button>
        <Button type="submit" variant="primary" busy={create.isPending} disabled={noClasses && form.classId === ''}>
          {t('stations.add.submit')}
        </Button>
      </div>
    </form>
  );
}
